import { z } from "zod";
import { Timestamps } from "../common/timestamps.schema.ts";
import { CustomerId } from "../fsm/fsm.schema.ts";
import { LocationId } from "../locations/locations.schema.ts";
import { StationId } from "../stations/stations.schema.ts";

export const BikeId = z.string().uuid().brand<"BikeId">();
export type BikeId = z.infer<typeof BikeId>;

export const BikeModelId = z.string().uuid().brand<"BikeModelId">();
export type BikeModelId = z.infer<typeof BikeModelId>;

export const BikeStatus = z.enum([
  "available",
  "in_repair",
  "in_transit",
  "en_garantia",
  "retired",
]);
export type BikeStatus = z.infer<typeof BikeStatus>;

// Canonical human-readable identifier for a bike model (e.g. IEB-2026)
export const BikeModelCode = z
  .string()
  .regex(/^[A-Z]{2,6}-\d{4}$/, "Formato inválido: XX-YYYY");
export type BikeModelCode = z.infer<typeof BikeModelCode>;

export const BikeModelDto = z
  .object({
    id: BikeModelId,
    customerId: CustomerId,
    model: z.string(),
    submodel: z.string().nullable(),
    code: BikeModelCode,
    isActive: z.boolean().default(true),
    deactivatedAt: z.string().nullable().optional(),
    deactivatedBy: z.string().uuid().nullable().optional(),
  })
  .merge(Timestamps);
export type BikeModelDto = z.infer<typeof BikeModelDto>;

export const BikeDto = z
  .object({
    id: BikeId,
    customerId: CustomerId,
    locationId: LocationId,
    // Additive (BEL-581/ADR 042): "currently parked at this station". Null
    // for every customer with zero stations (e.g. Ikos) — locationId is
    // unaffected and remains the hard, non-nullable FK.
    stationId: StationId.nullable(),
    modelId: BikeModelId,
    modelName: z.string().optional(), // Injected by join
    serialNumber: z.string().nullable(),
    beloraBikeId: z.string().nullable(),
    clientBaseNumber: z.string().nullable(),
    color: z.string().nullable(),
    status: BikeStatus,
    metadata: z.record(z.string(), z.unknown()),
    isActive: z.boolean().default(true),
    deactivatedAt: z.string().nullable().optional(),
    deactivatedBy: z.string().uuid().nullable().optional(),
  })
  .merge(Timestamps);
export type BikeDto = z.infer<typeof BikeDto>;

// Minimal-fields DTO for request-builder pickers (gated by requests.write, not admin.bikes.read)
export const BikePickerDto = z.object({
  id: BikeId,
  customerId: CustomerId,
  locationId: LocationId,
  modelId: BikeModelId,
  modelName: z.string().optional(),
  clientBaseNumber: z.string().nullable(),
  status: BikeStatus,
});
export type BikePickerDto = z.infer<typeof BikePickerDto>;

export const CreateBikeInput = z.object({
  customerId: CustomerId,
  locationId: LocationId,
  modelId: BikeModelId,
  serialNumber: z.string().min(1).optional(),
  beloraBikeId: z.string().min(1).optional(),
  clientBaseNumber: z.string().min(1).optional(),
  color: z.string().min(1).optional(),
  status: BikeStatus.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateBikeInput = z.infer<typeof CreateBikeInput>;

export const CreateBikeModelInput = z.object({
  model: z.string().min(1),
  submodel: z.string().min(1).optional(),
  code: BikeModelCode,
  customerId: CustomerId,
});
export type CreateBikeModelInput = z.infer<typeof CreateBikeModelInput>;

export const UpdateBikeModelInput = z.object({
  model: z.string().min(1),
  submodel: z.string().min(1).optional().nullable(),
  code: BikeModelCode.optional(),
});
export type UpdateBikeModelInput = z.infer<typeof UpdateBikeModelInput>;

export const AddBikeModelMaterialInput = z.object({
  materialId: z.string().uuid(),
});
export type AddBikeModelMaterialInput = z.infer<
  typeof AddBikeModelMaterialInput
>;

export const UpdateBikeInput = z.object({
  locationId: LocationId.optional(),
  modelId: BikeModelId.optional(),
  serialNumber: z.string().min(1).optional(),
  beloraBikeId: z.string().min(1).optional(),
  clientBaseNumber: z.string().min(1).optional(),
  color: z.string().min(1).optional(),
  status: BikeStatus.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type UpdateBikeInput = z.infer<typeof UpdateBikeInput>;
